import React, { useState, useEffect } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { Role } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { useCart } from '../../contexts/CartContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import CartDropdown from '../common/CartDropdown';
import FavoritesDropdown from '../common/FavoritesDropdown';
import Avatar from '../common/Avatar';
import Logo from '../common/Logo';

const { Link, NavLink, useNavigate } = ReactRouterDOM as any;

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { cartItems } = useCart();
  const { favorites } = useFavorites();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCartDropdownOpen, setIsCartDropdownOpen] = useState(false);
  const [isFavoritesDropdownOpen, setIsFavoritesDropdownOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();

  const dashboardPath = user?.role === Role.ADMIN ? '/admin' : '/dashboard';
  const isAdmin = user?.role === Role.ADMIN;
  
  const navLinkClasses = ({isActive}: {isActive: boolean}) => isActive ? "text-primary-600 dark:text-primary-400 font-semibold" : "text-gray-600 dark:text-gray-300 hover:text-primary-600";
  const mobileNavLinkClasses = ({isActive}: {isActive: boolean}) => `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`;

  const handleCartClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsCartDropdownOpen(!isCartDropdownOpen);
      setIsFavoritesDropdownOpen(false);
  }

  const handleFavoritesClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsFavoritesDropdownOpen(!isFavoritesDropdownOpen);
      setIsCartDropdownOpen(false);
  }

  const toggleLanguage = () => {
      const current = i18n.language;
      const next = current.startsWith('fr') ? 'en' : current.startsWith('en') ? 'ar' : 'fr';
      i18n.changeLanguage(next);
  };

  const getFlagUrl = (lang: string) => {
      if (lang.startsWith('ar')) return 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/tn.png';
      if (lang.startsWith('en')) return 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/gb.png';
      return 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/fr.png';
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 header-glass shadow-md transition-all duration-300">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
             <div className="md:hidden mr-2 rtl:mr-0 rtl:ml-2">
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <MenuIcon />
                </button>
            </div>
            <Link to="/" className="flex items-center flex-shrink-0">
              <Logo location="navbar" size="medium" />
            </Link>
          </div>

          <div className="hidden md:flex md:items-center gap-8">
            <NavLink to="/" className={navLinkClasses}>{t('nav.home')}</NavLink>
            <NavLink to="/services" className={navLinkClasses}>{t('nav.services')}</NavLink>
            <NavLink to="/contact" className={navLinkClasses}>{t('nav.contact')}</NavLink>
            <NavLink to="/about" className={navLinkClasses}>{t('nav.about')}</NavLink>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={toggleLanguage} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none">
                <img src={getFlagUrl(i18n.language)} alt={i18n.language} crossOrigin="anonymous" className="h-5 w-8 object-cover rounded-sm shadow-sm" />
            </button>

            <button onClick={toggleTheme} className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
              {isDarkMode ? <SunIcon /> : <MoonIcon />}
            </button>
            
            <div className="relative hidden md:block">
              <button onClick={handleFavoritesClick} className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 relative">
                <HeartIcon />
                {favorites.length > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white">
                    {favorites.length}
                    </span>
                )}
              </button>
              <FavoritesDropdown isOpen={isFavoritesDropdownOpen} onClose={() => setIsFavoritesDropdownOpen(false)} />
            </div>
            
            <div className="relative hidden md:block">
                <button onClick={handleCartClick} className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 relative">
                    <CartIcon />
                    {cartItems.length > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary-500 text-xs font-medium text-white">
                        {cartItems.length}
                        </span>
                    )}
                </button>
                <CartDropdown isOpen={isCartDropdownOpen} onClose={() => setIsCartDropdownOpen(false)} />
            </div>
            
            {user ? (
              <div className="relative">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} type="button" className="max-w-xs rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                  <Avatar user={user} className="h-10 w-10" />
                </button>
                {isUserMenuOpen && (
                  <div className="origin-top-right absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-10" onMouseLeave={() => setIsUserMenuOpen(false)}>
                    <div className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 border-b dark:border-gray-700">
                        <p className="font-semibold">{user.prénom} {user.nom}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <Link to={dashboardPath} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                       <ChartBarIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                       {t('common.dashboard')}
                    </Link>
                    <Link to={`${dashboardPath}/profile`} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <UserIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                        {t('common.profile')}
                    </Link>
                    <Link to={`${dashboardPath}/settings`} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <SettingsIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                        {t('common.settings')}
                    </Link>
                    <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                    <button onClick={logout} className="w-full text-left rtl:text-right flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <LogoutIcon className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                        {t('common.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                <LoginIcon />
              </Link>
            )}
          </div>
        </div>
        
        {isMenuOpen && (
             <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col space-y-2">
                    <NavLink to="/" className={mobileNavLinkClasses} onClick={() => setIsMenuOpen(false)}>{t('nav.home')}</NavLink>
                    <NavLink to="/services" className={mobileNavLinkClasses} onClick={() => setIsMenuOpen(false)}>{t('nav.services')}</NavLink>
                    <NavLink to="/contact" className={mobileNavLinkClasses} onClick={() => setIsMenuOpen(false)}>{t('nav.contact')}</NavLink>
                    <NavLink to="/about" className={mobileNavLinkClasses} onClick={() => setIsMenuOpen(false)}>{t('nav.about')}</NavLink>
                </div>
            </div>
        )}
      </nav>
    </header>
  );
};

const SunIcon: React.FC = () => <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M12 12a5 5 0 100-10 5 5 0 000 10z" /></svg>;
const MoonIcon: React.FC = () => <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>;
const HeartIcon: React.FC = () => <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" /></svg>;
const CartIcon: React.FC = () => <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
const LoginIcon: React.FC = () => <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>;
const MenuIcon: React.FC = () => <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" /></svg>;
const UserIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
const ChartBarIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
const SettingsIcon: React.FC<{className?:string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0 3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>);
const LogoutIcon: React.FC<{className?:string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>);

export default Navbar;